export type CprScreenPoint = { x: number; y: number };

export type CprOverlayDimensions = {
  width: number;
  height: number;
};

export type CprLabelMapForFill = {
  width: number;
  height: number;
  data: Uint8Array;
};

export function buildSmoothOpenPath(points: CprScreenPoint[]) {
  if (!points.length) return '';
  if (points.length === 1) return `M ${points[0].x} ${points[0].y}`;
  if (points.length === 2) {
    return `M ${points[0].x} ${points[0].y} L ${points[1].x} ${points[1].y}`;
  }

  let d = `M ${points[0].x} ${points[0].y}`;
  for (let i = 0; i < points.length - 1; i += 1) {
    const p0 = points[i - 1] ?? points[i];
    const p1 = points[i];
    const p2 = points[i + 1];
    const p3 = points[i + 2] ?? p2;

    const cp1x = p1.x + (p2.x - p0.x) / 6;
    const cp1y = p1.y + (p2.y - p0.y) / 6;
    const cp2x = p2.x - (p3.x - p1.x) / 6;
    const cp2y = p2.y - (p3.y - p1.y) / 6;

    d += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${p2.x} ${p2.y}`;
  }
  return d;
}

function appendPathToCanvas(
  ctx: CanvasRenderingContext2D,
  points: CprScreenPoint[],
  close: boolean,
) {
  if (!points.length) return;
  ctx.moveTo(points[0].x, points[0].y);
  for (let i = 1; i < points.length; i += 1) {
    ctx.lineTo(points[i].x, points[i].y);
  }
  if (close) ctx.closePath();
}

export function clipAnnulusOnCanvas(
  ctx: CanvasRenderingContext2D,
  wallSegments: CprScreenPoint[][],
  lumenSegments: CprScreenPoint[][],
) {
  const wallLeft = wallSegments[0] ?? [];
  const wallRight = wallSegments[1] ?? [];
  const lumenLeft = lumenSegments[0] ?? [];
  const lumenRight = lumenSegments[1] ?? [];

  if (
    wallLeft.length < 2 ||
    wallRight.length < 2 ||
    lumenLeft.length < 2 ||
    lumenRight.length < 2
  ) {
    return false;
  }

  const wallRing = [...wallLeft, ...[...wallRight].reverse()];
  const lumenRing = [...lumenLeft, ...[...lumenRight].reverse()];

  ctx.beginPath();
  appendPathToCanvas(ctx, wallRing, true);
  appendPathToCanvas(ctx, lumenRing, true);
  ctx.clip('evenodd');
  return true;
}

export type SolidMaskFillOptions = {
  labelMap: CprLabelMapForFill;
  mask: Uint8Array;
  rgba?: readonly [number, number, number, number];
};

export function createSolidMaskFillCanvas({
  labelMap,
  mask,
  rgba = [255, 155, 190, 175],
}: SolidMaskFillOptions) {
  const src = document.createElement('canvas');
  src.width = labelMap.width;
  src.height = labelMap.height;

  const ctx = src.getContext('2d');
  if (!ctx) return null;

  const img = ctx.createImageData(labelMap.width, labelMap.height);
  const out = img.data;
  const pixelCount = Math.min(mask.length, labelMap.width * labelMap.height);
  const [r, g, b, a] = rgba;

  for (let i = 0; i < pixelCount; i += 1) {
    if (mask[i] <= 0) continue;
    const p = i * 4;
    out[p + 0] = r;
    out[p + 1] = g;
    out[p + 2] = b;
    out[p + 3] = a;
  }

  ctx.putImageData(img, 0, 0);
  return src;
}

export function clipClosedRibbonOnCanvas(
  ctx: CanvasRenderingContext2D,
  segments: CprScreenPoint[][],
) {
  const left = segments[0] ?? [];
  const right = segments[1] ?? [];

  if (left.length < 2 || right.length < 2) return false;

  const ring = [...left, ...[...right].reverse()];
  ctx.beginPath();
  appendPathToCanvas(ctx, ring, true);
  ctx.clip();
  return true;
}

export function drawMaskFillCanvas({
  canvas,
  dimensions,
  labelMap,
  sourceCanvas,
  baseCorners,
  offset,
  clipSegments,
}: {
  canvas: HTMLCanvasElement;
  dimensions: CprOverlayDimensions;
  labelMap: CprLabelMapForFill;
  sourceCanvas: HTMLCanvasElement;
  baseCorners: CprScreenPoint[] | null;
  offset: CprScreenPoint;
  clipSegments?: CprScreenPoint[][];
}) {
  canvas.width = dimensions.width;
  canvas.height = dimensions.height;

  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.clearRect(0, 0, canvas.width, canvas.height);

  if (!baseCorners || baseCorners.length < 3) return;

  ctx.save();
  if (clipSegments && clipSegments.length > 0) {
    const clipped = clipClosedRibbonOnCanvas(ctx, clipSegments);
    if (!clipped) {
      ctx.restore();
      return;
    }
  }

  const p0 = {
    x: baseCorners[0].x + offset.x,
    y: baseCorners[0].y + offset.y,
  };
  const p1 = {
    x: baseCorners[1].x + offset.x,
    y: baseCorners[1].y + offset.y,
  };
  const p2 = {
    x: baseCorners[2].x + offset.x,
    y: baseCorners[2].y + offset.y,
  };

  const logicalW = labelMap.width;
  const logicalH = labelMap.height;

  const a = (p1.x - p0.x) / Math.max(1, logicalW);
  const b = (p1.y - p0.y) / Math.max(1, logicalW);
  const c = (p2.x - p0.x) / Math.max(1, logicalH);
  const d = (p2.y - p0.y) / Math.max(1, logicalH);

  ctx.setTransform(a, b, c, d, p0.x, p0.y);
  ctx.imageSmoothingEnabled = false;
  ctx.globalAlpha = 1;
  ctx.drawImage(sourceCanvas, 0, 0, logicalW, logicalH);
  ctx.restore();
  ctx.setTransform(1, 0, 0, 1, 0, 0);
}

export function clearCanvas(canvas: HTMLCanvasElement) {
  const ctx = canvas.getContext('2d');
  ctx?.clearRect(0, 0, canvas.width, canvas.height);
}

export function drawAnnulusFillCanvas({
  canvas,
  dimensions,
  labelMap,
  sourceCanvas,
  baseCorners,
  offset,
  wallSegments,
  lumenSegments,
}: {
  canvas: HTMLCanvasElement;
  dimensions: CprOverlayDimensions;
  labelMap: CprLabelMapForFill;
  sourceCanvas: HTMLCanvasElement;
  baseCorners: CprScreenPoint[] | null;
  offset: CprScreenPoint;
  wallSegments: CprScreenPoint[][];
  lumenSegments: CprScreenPoint[][];
}) {
  canvas.width = dimensions.width;
  canvas.height = dimensions.height;

  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.clearRect(0, 0, canvas.width, canvas.height);

  if (!baseCorners || baseCorners.length < 3) return;

  ctx.save();
  const clipped = clipAnnulusOnCanvas(ctx, wallSegments, lumenSegments);
  if (!clipped) {
    ctx.restore();
    return;
  }

  const p0 = {
    x: baseCorners[0].x + offset.x,
    y: baseCorners[0].y + offset.y,
  };
  const p1 = {
    x: baseCorners[1].x + offset.x,
    y: baseCorners[1].y + offset.y,
  };
  const p2 = {
    x: baseCorners[2].x + offset.x,
    y: baseCorners[2].y + offset.y,
  };

  const logicalW = labelMap.width;
  const logicalH = labelMap.height;

  const a = (p1.x - p0.x) / Math.max(1, logicalW);
  const b = (p1.y - p0.y) / Math.max(1, logicalW);
  const c = (p2.x - p0.x) / Math.max(1, logicalH);
  const d = (p2.y - p0.y) / Math.max(1, logicalH);

  ctx.setTransform(a, b, c, d, p0.x, p0.y);
  ctx.imageSmoothingEnabled = true;
  try {
    ctx.imageSmoothingQuality = 'high';
  } catch {}

  ctx.globalAlpha = 0.72;
  ctx.drawImage(sourceCanvas, 0, 0, logicalW, logicalH);
  ctx.globalAlpha = 1;
  ctx.drawImage(sourceCanvas, 0, 0, logicalW, logicalH);
  ctx.globalAlpha = 1;
  ctx.restore();
  ctx.setTransform(1, 0, 0, 1, 0, 0);
}

export function projectLocalPointToScreen({
  x,
  y,
  renderer,
  targetActor,
  dimensions,
}: {
  x: number;
  y: number;
  renderer: any;
  targetActor: any;
  dimensions: CprOverlayDimensions;
}): CprScreenPoint | null {
  const matrix = (targetActor?.getUserMatrix?.() as unknown as
    | ArrayLike<number>
    | undefined) ?? [
    1, 0, 0, 0, //
    0, 1, 0, 0, //
    0, 0, 1, 0, //
    0, 0, 0, 1,
  ];

  const aspect = dimensions.width / Math.max(1, dimensions.height);
  const worldX = matrix[0] * x + matrix[4] * y + matrix[12];
  const worldY = matrix[1] * x + matrix[5] * y + matrix[13];
  const worldZ = matrix[2] * x + matrix[6] * y + matrix[14];
  const worldW = matrix[3] * x + matrix[7] * y + matrix[15];
  const invW = worldW && Number.isFinite(worldW) ? 1 / worldW : 1;

  const displayPoint = renderer.worldToNormalizedDisplay(
    worldX * invW,
    worldY * invW,
    worldZ * invW,
    aspect,
  );

  if (!Number.isFinite(displayPoint[0]) || !Number.isFinite(displayPoint[1])) {
    return null;
  }

  return {
    x: displayPoint[0] * dimensions.width,
    y: displayPoint[1] * dimensions.height,
  };
}

export function projectLocalOutlinesToScreen(
  localOutlines: [number, number][][],
  renderer: any,
  targetActor: any,
  dimensions: CprOverlayDimensions,
) {
  return localOutlines
    .map((outline) =>
      outline
        .map(([lx, ly]) =>
          projectLocalPointToScreen({
            x: lx,
            y: ly,
            renderer,
            targetActor,
            dimensions,
          }),
        )
        .filter((point): point is CprScreenPoint => point !== null),
    )
    .filter((outline) => outline.length > 1);
}
